// Central registry for the Cold Compute / Neon series — ALL 24 videos in PUBLISH
// ORDER. The composition id carries the publish number (NN-) so the sequence is
// unambiguous in Remotion Studio and in `remotion render NN-Name`. Root.tsx maps
// over this array to emit one <Composition> + one <Still> per entry.
//
// Only the `id` strings carry the numbering — folders, component exports, and
// imports are unchanged.
import type { ComponentType } from "react";
import { builderNotebookEntries } from "./eakc-builder-notebook";

import { ElevenStageRagPipeline } from "./11-stage-rag-pipeline";
import { ElevenStageRagPipelineThumb } from "./11-stage-rag-pipeline/thumbnail";
import { TOTAL_DURATION as d01 } from "./11-stage-rag-pipeline/constants";
import { FiveProductionBugs } from "./five-production-bugs";
import { FiveProductionBugsThumb } from "./five-production-bugs/thumbnail";
import { TOTAL_DURATION as d02 } from "./five-production-bugs/constants";
import { McpVsCliWhyIChoseMcp } from "./mcp-vs-cli-why-i-chose-mcp";
import { McpVsCliWhyIChoseMcpThumb } from "./mcp-vs-cli-why-i-chose-mcp/thumbnail";
import { TOTAL_DURATION as d03 } from "./mcp-vs-cli-why-i-chose-mcp/constants";
import { WhyRagWorseThanNotebookLM } from "./why-rag-worse-than-notebooklm";
import { WhyRagWorseThanNotebookLMThumb } from "./why-rag-worse-than-notebooklm/thumbnail";
import { TOTAL_DURATION as d04 } from "./why-rag-worse-than-notebooklm/constants";
import { FullProductDemo } from "./full-product-demo";
import { FullProductDemoThumb } from "./full-product-demo/thumbnail";
import { TOTAL_DURATION as d05 } from "./full-product-demo/constants";
import { HonestSaasJourney } from "./honest-saas-journey";
import { HonestSaasJourneyThumb } from "./honest-saas-journey/thumbnail";
import { TOTAL_DURATION as d06 } from "./honest-saas-journey/constants";
import { NoveltyDedupSaidThatAlready } from "./novelty-dedup-said-that-already";
import { NoveltyDedupSaidThatAlreadyThumb } from "./novelty-dedup-said-that-already/thumbnail";
import { TOTAL_DURATION as d07 } from "./novelty-dedup-said-that-already/constants";
import { RunawayLoop55Incident } from "./55-runaway-loop-incident";
import { RunawayLoop55IncidentThumb } from "./55-runaway-loop-incident/thumbnail";
import { TOTAL_DURATION as d08 } from "./55-runaway-loop-incident/constants";
import { MultiTenantSevenLayers } from "./multi-tenant-7-layers";
import { MultiTenantSevenLayersThumb } from "./multi-tenant-7-layers/thumbnail";
import { TOTAL_DURATION as d09 } from "./multi-tenant-7-layers/constants";
import { RagAccuracy75To90 } from "./rag-accuracy-75-to-90";
import { RagAccuracy75To90Thumb } from "./rag-accuracy-75-to-90/thumbnail";
import { TOTAL_DURATION as d10 } from "./rag-accuracy-75-to-90/constants";
import { OneOfTwelveNozzles } from "./one-of-twelve-nozzles-honest";
import { OneOfTwelveNozzlesThumb } from "./one-of-twelve-nozzles-honest/thumbnail";
import { TOTAL_DURATION as d11 } from "./one-of-twelve-nozzles-honest/constants";
import { NorthStarHonestRoadmap } from "./north-star-honest-roadmap";
import { NorthStarHonestRoadmapThumb } from "./north-star-honest-roadmap/thumbnail";
import { TOTAL_DURATION as d12 } from "./north-star-honest-roadmap/constants";
import { SmartProcessPicksOwnModel } from "./smart-process-picks-own-model";
import { SmartProcessPicksOwnModelThumb } from "./smart-process-picks-own-model/thumbnail";
import { TOTAL_DURATION as d13 } from "./smart-process-picks-own-model/constants";
import { LastTenPercentIsTrust } from "./last-10-percent-is-trust";
import { LastTenPercentIsTrustThumb } from "./last-10-percent-is-trust/thumbnail";
import { TOTAL_DURATION as d14 } from "./last-10-percent-is-trust/constants";
import { CompanyBrainNotThePhone } from "./company-brain-not-the-phone";
import { CompanyBrainNotThePhoneThumb } from "./company-brain-not-the-phone/thumbnail";
import { TOTAL_DURATION as d15 } from "./company-brain-not-the-phone/constants";
import { A2uiAgentGeneratedUI } from "./a2ui-agent-generated-ui";
import { A2uiAgentGeneratedUIThumb } from "./a2ui-agent-generated-ui/thumbnail";
import { TOTAL_DURATION as d16 } from "./a2ui-agent-generated-ui/constants";
import { SkillsAutoFromChat } from "./skills-auto-from-chat";
import { SkillsAutoFromChatThumb } from "./skills-auto-from-chat/thumbnail";
import { TOTAL_DURATION as d17 } from "./skills-auto-from-chat/constants";
import { RrfCragEmbeddingDimensions } from "./rrf-crag-1536-dimensions";
import { RrfCragEmbeddingDimensionsThumb } from "./rrf-crag-1536-dimensions/thumbnail";
import { TOTAL_DURATION as d18 } from "./rrf-crag-1536-dimensions/constants";
import { SyncEngineArchitecture } from "./sync-engine-architecture";
import { SyncEngineArchitectureThumb } from "./sync-engine-architecture/thumbnail";
import { TOTAL_DURATION as d19 } from "./sync-engine-architecture/constants";
import { MutualRecursionFakeOom } from "./mutual-recursion-fake-oom";
import { MutualRecursionFakeOomThumb } from "./mutual-recursion-fake-oom/thumbnail";
import { TOTAL_DURATION as d20 } from "./mutual-recursion-fake-oom/constants";
import { IntegrationProvidersOneInterface } from "./integration-providers-one-interface";
import { IntegrationProvidersOneInterfaceThumb } from "./integration-providers-one-interface/thumbnail";
import { TOTAL_DURATION as d21 } from "./integration-providers-one-interface/constants";
import { McpServerWeekend } from "./mcp-server-weekend";
import { McpServerWeekendThumb } from "./mcp-server-weekend/thumbnail";
import { TOTAL_DURATION as d22 } from "./mcp-server-weekend/constants";
import { FlagshipNaiveRagFails } from "./flagship-naive-rag-fails";
import { FlagshipNaiveRagFailsThumb } from "./flagship-naive-rag-fails/thumbnail";
import { TOTAL_DURATION as dFlag } from "./flagship-naive-rag-fails/constants";
import { HybridSearchVectorBM25 } from "./hybrid-search-vector-bm25";
import { HybridSearchThumb } from "./hybrid-search-vector-bm25/thumbnail";
import { TOTAL_DURATION as dHybrid } from "./hybrid-search-vector-bm25/constants";
// — added from today's Content Idea Generator emails (re-grounded; fabrications dropped) —
import { PiiRedactionBeforeLLM } from "./pii-redaction-before-llm";
import { PiiRedactionBeforeLLMThumb } from "./pii-redaction-before-llm/thumbnail";
import { TOTAL_DURATION as d25 } from "./pii-redaction-before-llm/constants";
import { ParallelAgentExecution } from "./parallel-agent-execution";
import { ParallelAgentExecutionThumb } from "./parallel-agent-execution/thumbnail";
import { TOTAL_DURATION as d26 } from "./parallel-agent-execution/constants";
import { MissionAgentCoordination } from "./mission-agent-coordination";
import { MissionAgentCoordinationThumb } from "./mission-agent-coordination/thumbnail";
import { TOTAL_DURATION as d27 } from "./mission-agent-coordination/constants";
// — from the LATEST (now-grounded) Content Idea Generator emails —
import { AdaptiveChunking } from "./adaptive-chunking";
import { AdaptiveChunkingThumb } from "./adaptive-chunking/thumbnail";
import { TOTAL_DURATION as d28 } from "./adaptive-chunking/constants";
import { GraphQueryCodeIntel } from "./graph-query-code-intelligence";
import { GraphQueryCodeIntelThumb } from "./graph-query-code-intelligence/thumbnail";
import { TOTAL_DURATION as d29 } from "./graph-query-code-intelligence/constants";
import { GdprUserDataExport } from "./gdpr-user-data-export";
import { GdprUserDataExportThumb } from "./gdpr-user-data-export/thumbnail";
import { TOTAL_DURATION as d30 } from "./gdpr-user-data-export/constants";
import { CapabilityInference } from "./capability-inference";
import { CapabilityInferenceThumb } from "./capability-inference/thumbnail";
import { TOTAL_DURATION as d31 } from "./capability-inference/constants";
// — companion to docs/articles/2026-06-04-rag-is-burning-money-cost-control.md —
import { RagBurningMoneyCostLayer } from "./rag-is-burning-money";
import { RagBurningMoneyCostLayerThumb } from "./rag-is-burning-money/thumbnail";
import { TOTAL_DURATION as d32 } from "./rag-is-burning-money/constants";
import { The13000TokenBug } from "./the-13000-token-bug";
import { The13000TokenBugThumb } from "./the-13000-token-bug/thumbnail";
import { TOTAL_DURATION as d33 } from "./the-13000-token-bug/constants";

// — companion to docs/incidents/2026-06-10-traefik-outage-postmortem.md —
import { TraefikVanishedOutage } from "./traefik-vanished-outage";
import { TraefikVanishedOutageThumb } from "./traefik-vanished-outage/thumbnail";
import { TOTAL_DURATION as d34 } from "./traefik-vanished-outage/constants";
// — incident/debugging episode: the pgvector HNSW search timeout (2026-06-11) —
import { HybridSearchHnswTimeout } from "./hybrid-search-hnsw-timeout";
import { HybridSearchHnswTimeoutThumb } from "./hybrid-search-hnsw-timeout/thumbnail";
import { TOTAL_DURATION as d35 } from "./hybrid-search-hnsw-timeout/constants";
// — incident/polish episode: four "works but wrong" RAG fixes (2026-06-12) —
import { RagPolishWorksButWrong } from "./rag-polish-works-but-wrong";
import { RagPolishWorksButWrongThumb } from "./rag-polish-works-but-wrong/thumbnail";
import { TOTAL_DURATION as d36 } from "./rag-polish-works-but-wrong/constants";
// — measure-then-cut episode: stop tuning RAG by vibes (2026-06-14) —
import { MeasureDontGuessRag } from "./measure-dont-guess-rag";
import { MeasureDontGuessRagThumb } from "./measure-dont-guess-rag/thumbnail";
import { TOTAL_DURATION as d37 } from "./measure-dont-guess-rag/constants";
// — chat answer-quality session: one question, four distinct bugs (2026-06-15) —
import { RagFourWaysWrong } from "./rag-four-ways-wrong";
import { RagFourWaysWrongThumb } from "./rag-four-ways-wrong/thumbnail";
import { TOTAL_DURATION as d38 } from "./rag-four-ways-wrong/constants";
// — planning + phased build of EAKC's Action-Oriented Dynamic UI (A2UI v2), 2026-06-23 —
import { ActionOrientedDynamicUI } from "./action-oriented-dynamic-ui";
import { ActionOrientedDynamicUIThumb } from "./action-oriented-dynamic-ui/thumbnail";
import { TOTAL_DURATION as d39 } from "./action-oriented-dynamic-ui/constants";
// — incident/debugging episode: DB vanished + the zombie sync lock (2026-07-04) —
import { DatabaseVanishedZombieLock } from "./database-vanished-zombie-lock";
import { DatabaseVanishedZombieLockThumb } from "./database-vanished-zombie-lock/thumbnail";
import { TOTAL_DURATION as d40 } from "./database-vanished-zombie-lock/constants";
// feature/build episode: auto-capture mission output into a searchable KB (2026-07-09)
import { MissionMemoryAutoCapture } from "./mission-memory-auto-capture";
import { MissionMemoryAutoCaptureThumb } from "./mission-memory-auto-capture/thumbnail";
import { TOTAL_DURATION as d41 } from "./mission-memory-auto-capture/constants";
// solo-dev/educational episode: source-diagram passthrough — trust an AI-generated diagram? (2026-07-12)
import { TrustAiGeneratedDiagram } from "./trust-ai-generated-diagram";
import { TrustAiGeneratedDiagramThumb } from "./trust-ai-generated-diagram/thumbnail";
import { TOTAL_DURATION as d42 } from "./trust-ai-generated-diagram/constants";
// build-in-public/quality episode: the quality layer shipped (not a victory lap) — grounded answers,
// reliable chat, grounded quizzes, operator control + honest "still being measured" close (2026-07-19)
import { TheQualityLayerShipped } from "./the-quality-layer-shipped";
import { TheQualityLayerShippedThumb } from "./the-quality-layer-shipped/thumbnail";
import { TOTAL_DURATION as d43 } from "./the-quality-layer-shipped/constants";
// honest debugging episode: audited one prod chat — most "bugs" were false alarms; the one
// real leak (internal chunk-map markers) was fixed a layer upstream of the first attempt (2026-07)
import { DebugTheRealPath } from "./debug-the-real-path";
import { DebugTheRealPathThumb } from "./debug-the-real-path/thumbnail";
import { TOTAL_DURATION as d45 } from "./debug-the-real-path/constants";
// ep 46: the reranker is the confidently-wrong-#1 fixer. Swapped the in-process LLM
// cross-encoder EMULATION (BATCH_SIZE=5, ~10 calls/query) for Voyage rerank-2.5 (1 call,
// flag-gated RERANK_PROVIDER, fail-open). A/B = quality parity, ~2× faster; the honest
// catch is the 429 fail-open and the trace instrument that now names which scorer ran.
import { VoyageRerankerCrossEncoder } from "./voyage-reranker-cross-encoder";
import { VoyageRerankerCrossEncoderThumb } from "./voyage-reranker-cross-encoder/thumbnail";
import { TOTAL_DURATION as d46 } from "./voyage-reranker-cross-encoder/constants";
// market-analysis/build-in-public episode: the agentic-web receipts week (Claudeforce, $1.5B
// Agentforce ARR, MCP Apps, Stripe→OpenRouter) vs EAKC's three-point vision. FIRST episode
// with a real recorded VO track (public/vo/47/); assembled with drop-in placeholders (2026-08-30)
import { AppsAreDyingReceipts } from "./apps-are-dying-receipts";
import { AppsAreDyingReceiptsThumb } from "./apps-are-dying-receipts/thumbnail";
import { TOTAL_DURATION as d47 } from "./apps-are-dying-receipts/constants";
// eval episode: the payoff to ep 43's "waiting to be measured" close — the retriever-vs-generator
// gate was broken FOUR times in one day (0 of 15 cases labeled → 50%; corpus probe on a missing
// column → 66.7%; the multi-hop half hardcoded false → a real 0.0%; the harness stopping at top 5
// while production hands the model 8/15/20 → 62.5%, passing 4 of 15 → 7 of 15). Every broken
// version looked like a real product finding (2026-07-19)
import { EvalGateCouldntSee } from "./eval-gate-couldnt-see";
import { EvalGateCouldntSeeThumb } from "./eval-gate-couldnt-see/thumbnail";
import { TOTAL_DURATION as d44 } from "./eval-gate-couldnt-see/constants";

export interface VideoEntry {
  id: string;
  component: ComponentType;
  thumb: ComponentType;
  duration: number;
}

// PUBLISH ORDER (01–24). Reorder this array to change the publish sequence; the
// number prefixes and Studio order follow it automatically.
export const videoRegistry: VideoEntry[] = [
  { id: "01-ElevenStageRagPipeline", component: ElevenStageRagPipeline, thumb: ElevenStageRagPipelineThumb, duration: d01 },
  { id: "02-FlagshipNaiveRagFails", component: FlagshipNaiveRagFails, thumb: FlagshipNaiveRagFailsThumb, duration: dFlag },
  { id: "03-McpVsCliWhyIChoseMcp", component: McpVsCliWhyIChoseMcp, thumb: McpVsCliWhyIChoseMcpThumb, duration: d03 },
  { id: "04-FiveProductionBugs", component: FiveProductionBugs, thumb: FiveProductionBugsThumb, duration: d02 },
  { id: "05-HybridSearchVectorBM25", component: HybridSearchVectorBM25, thumb: HybridSearchThumb, duration: dHybrid },
  { id: "06-RagAccuracy75To90", component: RagAccuracy75To90, thumb: RagAccuracy75To90Thumb, duration: d10 },
  { id: "07-WhyRagWorseThanNotebookLM", component: WhyRagWorseThanNotebookLM, thumb: WhyRagWorseThanNotebookLMThumb, duration: d04 },
  { id: "08-RrfCragEmbeddingDimensions", component: RrfCragEmbeddingDimensions, thumb: RrfCragEmbeddingDimensionsThumb, duration: d18 },
  { id: "09-SyncEngineArchitecture", component: SyncEngineArchitecture, thumb: SyncEngineArchitectureThumb, duration: d19 },
  { id: "10-MutualRecursionFakeOom", component: MutualRecursionFakeOom, thumb: MutualRecursionFakeOomThumb, duration: d20 },
  { id: "11-MultiTenantSevenLayers", component: MultiTenantSevenLayers, thumb: MultiTenantSevenLayersThumb, duration: d09 },
  { id: "12-McpServerWeekend", component: McpServerWeekend, thumb: McpServerWeekendThumb, duration: d22 },
  { id: "13-IntegrationProvidersOneInterface", component: IntegrationProvidersOneInterface, thumb: IntegrationProvidersOneInterfaceThumb, duration: d21 },
  { id: "14-FullProductDemo", component: FullProductDemo, thumb: FullProductDemoThumb, duration: d05 },
  { id: "15-NoveltyDedupSaidThatAlready", component: NoveltyDedupSaidThatAlready, thumb: NoveltyDedupSaidThatAlreadyThumb, duration: d07 },
  { id: "16-SmartProcessPicksOwnModel", component: SmartProcessPicksOwnModel, thumb: SmartProcessPicksOwnModelThumb, duration: d13 },
  { id: "17-RunawayLoop55Incident", component: RunawayLoop55Incident, thumb: RunawayLoop55IncidentThumb, duration: d08 },
  { id: "18-SkillsAutoFromChat", component: SkillsAutoFromChat, thumb: SkillsAutoFromChatThumb, duration: d17 },
  { id: "19-OneOfTwelveNozzles", component: OneOfTwelveNozzles, thumb: OneOfTwelveNozzlesThumb, duration: d11 },
  { id: "20-LastTenPercentIsTrust", component: LastTenPercentIsTrust, thumb: LastTenPercentIsTrustThumb, duration: d14 },
  { id: "21-HonestSaasJourney", component: HonestSaasJourney, thumb: HonestSaasJourneyThumb, duration: d06 },
  { id: "22-CompanyBrainNotThePhone", component: CompanyBrainNotThePhone, thumb: CompanyBrainNotThePhoneThumb, duration: d15 },
  { id: "23-NorthStarHonestRoadmap", component: NorthStarHonestRoadmap, thumb: NorthStarHonestRoadmapThumb, duration: d12 },
  { id: "24-A2uiAgentGeneratedUI", component: A2uiAgentGeneratedUI, thumb: A2uiAgentGeneratedUIThumb, duration: d16 },
  // — from today's Content Idea Generator emails, re-grounded against the real repo —
  { id: "25-PiiRedactionBeforeLLM", component: PiiRedactionBeforeLLM, thumb: PiiRedactionBeforeLLMThumb, duration: d25 },
  { id: "26-ParallelAgentExecution", component: ParallelAgentExecution, thumb: ParallelAgentExecutionThumb, duration: d26 },
  { id: "27-MissionAgentCoordination", component: MissionAgentCoordination, thumb: MissionAgentCoordinationThumb, duration: d27 },
  // — from the latest now-grounded Content Idea Generator emails (2026-06-04) —
  { id: "28-AdaptiveChunking", component: AdaptiveChunking, thumb: AdaptiveChunkingThumb, duration: d28 },
  { id: "29-GraphQueryCodeIntel", component: GraphQueryCodeIntel, thumb: GraphQueryCodeIntelThumb, duration: d29 },
  { id: "30-GdprUserDataExport", component: GdprUserDataExport, thumb: GdprUserDataExportThumb, duration: d30 },
  { id: "31-CapabilityInference", component: CapabilityInference, thumb: CapabilityInferenceThumb, duration: d31 },
  // — companion video to the cost-control deep-dive article (2026-06-04) —
  { id: "32-RagBurningMoneyCostLayer", component: RagBurningMoneyCostLayer, thumb: RagBurningMoneyCostLayerThumb, duration: d32 },
  // — Building EAKC's Self-Evolving Brain series (2026-06-09 base-hardening session) —
  { id: "33-The13000TokenBug", component: The13000TokenBug, thumb: The13000TokenBugThumb, duration: d33 },
  // — incident/debugging episode: the Dokploy Traefik outage (2026-06-10) —
  { id: "34-TraefikVanishedOutage", component: TraefikVanishedOutage, thumb: TraefikVanishedOutageThumb, duration: d34 },
  // — incident/debugging episode: "The 6-Second Search" pgvector HNSW timeout (2026-06-11) —
  { id: "35-HybridSearchHnswTimeout", component: HybridSearchHnswTimeout, thumb: HybridSearchHnswTimeoutThumb, duration: d35 },
  // — incident/polish episode: four "works but wrong" RAG fixes (2026-06-12) —
  { id: "36-RagPolishWorksButWrong", component: RagPolishWorksButWrong, thumb: RagPolishWorksButWrongThumb, duration: d36 },
  // — measure-then-cut episode: stop tuning RAG by vibes (2026-06-14) —
  { id: "37-MeasureDontGuessRag", component: MeasureDontGuessRag, thumb: MeasureDontGuessRagThumb, duration: d37 },
  // — chat answer-quality session: real product UI of four bugs + fixes (2026-06-15) —
  { id: "38-RagFourWaysWrong", component: RagFourWaysWrong, thumb: RagFourWaysWrongThumb, duration: d38 },
  // — planning + phased build of EAKC's Action-Oriented Dynamic UI (A2UI v2), 2026-06-23 —
  { id: "39-ActionOrientedDynamicUI", component: ActionOrientedDynamicUI, thumb: ActionOrientedDynamicUIThumb, duration: d39 },
  // — incident/debugging episode: DB vanished + the zombie sync lock (2026-07-04) —
  { id: "40-DatabaseVanishedZombieLock", component: DatabaseVanishedZombieLock, thumb: DatabaseVanishedZombieLockThumb, duration: d40 },
  // feature/build episode: auto-capture mission output into a searchable KB (2026-07-09)
  { id: "41-MissionMemoryAutoCapture", component: MissionMemoryAutoCapture, thumb: MissionMemoryAutoCaptureThumb, duration: d41 },
  // solo-dev/educational episode: source-diagram passthrough — can you trust an AI-generated diagram? (2026-07-12)
  { id: "42-TrustAiGeneratedDiagram", component: TrustAiGeneratedDiagram, thumb: TrustAiGeneratedDiagramThumb, duration: d42 },
  // build-in-public/quality episode: the quality layer shipped (not a victory lap) — grounded answers,
  // reliable chat, grounded quizzes, operator control + honest "still being measured" close (2026-07-19)
  { id: "43-TheQualityLayerShipped", component: TheQualityLayerShipped, thumb: TheQualityLayerShippedThumb, duration: d43 },
  // eval episode: the DIRECT payoff to ep 43's "waiting to be measured" close, so it must publish
  // immediately after 43 (its opening line says "Last episode..."). The gate read 0% retriever-side
  // because 0 of 15 golden cases carried the label its math needs; labeled + corpus probe fixed
  // → the real split is 66.7% retriever-side, 6 of 9 failures (2026-07-19)
  { id: "44-EvalGateCouldntSee", component: EvalGateCouldntSee, thumb: EvalGateCouldntSeeThumb, duration: d44 },
  // honest debugging episode: most flagged "bugs" were false alarms; the one real leak was fixed
  // a layer upstream of the first attempt — verified on prod 4/9/25 → 0/0/0 (2026-07).
  // Standalone: references neither neighbour, so it sits after the 43→44 promise/payoff pair.
  { id: "45-DebugTheRealPath", component: DebugTheRealPath, thumb: DebugTheRealPathThumb, duration: d45 },
  { id: "46-VoyageRerankerCrossEncoder", component: VoyageRerankerCrossEncoder, thumb: VoyageRerankerCrossEncoderThumb, duration: d46 },
  // recorded-VO assembly with drop-in placeholders — see apps-are-dying-receipts/metadata.md
  { id: "47-AppsAreDyingReceipts", component: AppsAreDyingReceipts, thumb: AppsAreDyingReceiptsThumb, duration: d47 },
  // Episodes48–53: source-grounded builder notebook, with saved Prabhat-profile
  // narration. Each is a normal editable Composition + thumbnail in Studio.
  ...builderNotebookEntries,
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
